import { Children, isValidElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function optionText(children) {
  if (children === null || children === undefined) return "";
  if (Array.isArray(children)) return children.map(optionText).join("");
  return String(children);
}

function normalizeOptions(options = [], children = null) {
  if (children) {
    return Children.toArray(children)
      .filter((child) => isValidElement(child) && child.type === "option")
      .map((child) => {
        const label = optionText(child.props.children);
        const value = child.props.value ?? label;
        return { value: String(value), label, disabled: !!child.props.disabled };
      });
  }
  return (options || []).map((option) => {
    if (typeof option === "string" || typeof option === "number" || typeof option === "boolean") {
      return { value: String(option), label: String(option), disabled: false };
    }
    const value = option?.value ?? "";
    return {
      value: String(value),
      label: option?.label ?? option?.description ?? String(value),
      disabled: !!option?.disabled,
      displayLabel: option?.displayLabel ?? option?.label ?? option?.description ?? String(value),
    };
  });
}

export default function VestaSelect({
  value,
  options,
  children,
  onChange,
  onValueChange,
  onBlur,
  className = "",
  "aria-label": nativeAriaLabel,
  ariaLabel,
  disabled = false,
  placeholder = "Seleziona",
  name,
}) {
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const typeBufferRef = useRef("");
  const typeBufferTimeRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const safeOptions = useMemo(() => normalizeOptions(options, children), [options, children]);
  const selected = safeOptions.find((option) => String(option.value) === String(value));
  const displayLabel = selected?.displayLabel || selected?.label || placeholder;
  const effectiveAriaLabel = nativeAriaLabel || ariaLabel || placeholder || "Seleziona valore";

  function syncMenuPosition() {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) return;
    const rect = button.getBoundingClientRect();
    menu.style.setProperty("--vesta-select-left", `${rect.left}px`);
    menu.style.setProperty("--vesta-select-width", `${rect.width}px`);
    const menuWidth = menu.getBoundingClientRect().width;
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - menuWidth - 8));
    const itemHeight = 40;
    const menuChrome = 18;
    const maxVisibleItems = 4;
    const estimatedHeight = Math.min(menuChrome + safeOptions.length * itemHeight, menuChrome + maxVisibleItems * itemHeight);
    const availableBelow = window.innerHeight - rect.bottom - 8;
    const insideOverlay = Boolean(button.closest(".overlay_card"));
    const openUp = !insideOverlay && availableBelow < estimatedHeight && rect.top > availableBelow;
    const top = openUp ? Math.max(8, rect.top - estimatedHeight - 3) : rect.bottom + 3;
    menu.style.setProperty("--vesta-select-top", `${top}px`);
    menu.style.setProperty("--vesta-select-left", `${left}px`);
    setReady(true);
  }

  useLayoutEffect(() => {
    if (!open) return undefined;
    setReady(false);
    syncMenuPosition();
    return undefined;
  }, [open, safeOptions.length]);

  useEffect(() => {
    if (!open) return undefined;
    function closeOnOutside(event) {
      if (wrapRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }
    function reposition() {
      syncMenuPosition();
    }
    const insideOverlay = Boolean(buttonRef.current?.closest(".overlay_card"));
    const trigger = buttonRef.current;
    const menu = menuRef.current;
    const menuScroller = menu?.querySelector(".vesta_select_scroll");
    function routeOverlayWheel(event) {
      if (!insideOverlay || !menuScroller) return;
      event.preventDefault();
      event.stopPropagation();
      const wheelUnit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(menuScroller.clientHeight, 1) : 1;
      menuScroller.scrollTop += event.deltaY * wheelUnit;
    }
    function closeDropdown() {
      setOpen(false);
    }
    function closeOnPageScroll(event) {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutside, true);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("vesta:close-dropdowns", closeDropdown);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", closeOnPageScroll, true);
    if (insideOverlay) {
      trigger?.addEventListener("wheel", routeOverlayWheel, { passive: false });
      menu?.addEventListener("wheel", routeOverlayWheel, { passive: false });
    }
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside, true);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("vesta:close-dropdowns", closeDropdown);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", closeOnPageScroll, true);
      trigger?.removeEventListener("wheel", routeOverlayWheel);
      menu?.removeEventListener("wheel", routeOverlayWheel);
    };
  }, [open, safeOptions.length]);

  function emitValue(nextValue) {
    onValueChange?.(nextValue);
    onChange?.({ target: { value: nextValue, name }, currentTarget: { value: nextValue, name } });
  }

  function choose(option) {
    if (option.disabled) return;
    emitValue(String(option.value ?? ""));
    setOpen(false);
    buttonRef.current?.focus();
  }

  function clearValue(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (disabled || String(value ?? "") === "") return;
    emitValue("");
    setOpen(false);
    buttonRef.current?.focus();
  }

  function handleBlur() {
    if (!onBlur) return;
    window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (wrapRef.current?.contains(activeElement) || menuRef.current?.contains(activeElement)) return;
      onBlur();
    }, 0);
  }

  function handleTypeAhead(event) {
    if (disabled || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const targetIsOption = event.target?.closest?.(".vesta_select_option");
    if (targetIsOption && (event.key === "Enter" || event.key === " ")) return;
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    const now = Date.now();
    const typeBufferActive = now - typeBufferTimeRef.current <= 850 && typeBufferRef.current.length > 0;
    if (event.key === "Enter" || (event.key === " " && !open && !typeBufferActive)) {
      event.preventDefault();
      setOpen((current) => !current);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (event.key !== "Backspace" && event.key.length !== 1) return;

    const expired = now - typeBufferTimeRef.current > 850;
    typeBufferTimeRef.current = now;
    typeBufferRef.current = event.key === "Backspace"
      ? (expired ? "" : typeBufferRef.current.slice(0, -1))
      : `${expired ? "" : typeBufferRef.current}${event.key}`;

    if (event.key === " ") event.preventDefault();
    const typed = typeBufferRef.current.trim().toLocaleLowerCase("it");
    if (!typed) return;
    const match = safeOptions.find((option) => !option.disabled && String(option.label || "").trim().toLocaleLowerCase("it").startsWith(typed));
    if (!match) return;
    event.preventDefault();
    emitValue(String(match.value ?? ""));
  }

  return (
    <div className={`vesta_select ${className}`.trim()} ref={wrapRef}>
      <div className="vesta_select_control">
        <button
          type="button"
          ref={buttonRef}
          className={`vesta_select_trigger${selected ? "" : " is_placeholder"}`}
          onClick={() => !disabled && setOpen((current) => !current)}
          onKeyDown={handleTypeAhead}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={effectiveAriaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-vesta-value={String(value ?? "")}
        >
          <span>{displayLabel}</span>
          <span className="vesta_select_chevron" aria-hidden="true"></span>
        </button>
        <button
          type="button"
          className="vesta_select_clear_button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={clearValue}
          disabled={disabled || String(value ?? "") === ""}
          aria-label={`Azzera ${effectiveAriaLabel}`}
          title="Azzera valore"
          tabIndex={-1}
        >
          <span className="vesta_select_clear_icon" aria-hidden="true">0</span>
        </button>
      </div>
      {open && createPortal(
        <div ref={menuRef} className={`vesta_select_menu${ready ? " is_ready" : ""}`} role="listbox" aria-label={effectiveAriaLabel} onKeyDown={handleTypeAhead}>
          <div className="vesta_select_scroll">
            {safeOptions.map((option, index) => {
              const active = String(option.value) === String(value);
              return (
                <button
                  key={`${String(option.value)}-${String(option.label)}-${index}`}
                  type="button"
                  className={`vesta_select_option${active ? " is_active" : ""}`}
                  onClick={() => choose(option)}
                  role="option"
                  aria-selected={active}
                  disabled={option.disabled}
                >
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
